import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  PaperProps,
  Grid,
} from "@mui/material";
import TimerIcon from "@mui/icons-material/Timer";
import Draggable from "react-draggable";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import PatientService from "../../services/PatientService";
import { UserContext } from "../../context/UserContext";

// Custom Paper component for draggable Dialogs
function DraggablePaper(props: PaperProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable
      handle=".time-tracker-header"
      nodeRef={nodeRef}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

interface PersistedTimeTrackerState {
  sessionStatus: "running" | "review";
  sessionStartTime: number;
  lastModeSwitchTime: number;
  currentMode: "rpm-prep" | "rpm-call" | "rpm-wrap";
  rpmPrepTime: number; // in seconds
  rpmCallTime: number; // in seconds
  rpmWrapTime: number; // in seconds
  savedAt: number;
}

type SessionStatus = "idle" | "running" | "review";

interface TimeTrackerProps {
  patientId: string;
  open: boolean; // used to initially show the modal (when idle/review)
  onClose: (PatientId: string | null, utc_time: string | null) => void;
}

const TimeTracker: React.FC<TimeTrackerProps> = ({ patientId, open, onClose }) => {
  const [cookies] = useCookies(["access_token"]);
  const userCtx = useContext(UserContext);
  const { t } = useTranslation();
  const timeTrackerDialogRef = useRef<HTMLDivElement>(null);

  // --- State Variables ---
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>("idle");
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [lastModeSwitchTime, setLastModeSwitchTime] = useState<number | null>(null);
  const [currentMode, setCurrentMode] = useState<"rpm-prep" | "rpm-call" | "rpm-wrap">("rpm-prep");
  const [rpmPrepTime, setRpmPrepTime] = useState<number>(0);
  const [rpmCallTime, setRpmCallTime] = useState<number>(0);
  const [rpmWrapTime, setRpmWrapTime] = useState<number>(0);
  const [, setTick] = useState<number>(Date.now());

  // --- Mode Panel Rendering ---
  const renderModePanels = () => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2} justifyContent="center">
        {(["rpm-prep", "rpm-call", "rpm-wrap"] as const).map((mode) => {
          let label = "";
          let time = 0;
          switch (mode) {
            case "rpm-prep":
              label = t("RPM Prep");
              time =
                currentMode === "rpm-prep" && sessionStatus === "running"
                  ? rpmPrepTime +
                    (lastModeSwitchTime ? Math.floor((Date.now() - lastModeSwitchTime) / 1000) : 0)
                  : rpmPrepTime;
              break;
            case "rpm-call":
              label = t("RPM Call");
              time =
                currentMode === "rpm-call" && sessionStatus === "running"
                  ? rpmCallTime +
                    (lastModeSwitchTime ? Math.floor((Date.now() - lastModeSwitchTime) / 1000) : 0)
                  : rpmCallTime;
              break;
            case "rpm-wrap":
              label = t("RPM Wrap");
              time =
                currentMode === "rpm-wrap" && sessionStatus === "running"
                  ? rpmWrapTime +
                    (lastModeSwitchTime ? Math.floor((Date.now() - lastModeSwitchTime) / 1000) : 0)
                  : rpmWrapTime;
              break;
          }
          return (
            <Grid item xs={4} key={mode}>
              <Paper
                onClick={() => handleModeSwitch(mode)}
                sx={{
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  border: currentMode === mode ? "4px solid" : "1px solid",
                  borderColor: currentMode === mode ? "primary.main" : "grey.300",
                }}
              >
                <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
                  {label}
                </Typography>
                <Typography variant="h6">{formatTime(time)}</Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // --- Load persisted state on mount ---
  useEffect(() => {
    const persistedStr = localStorage.getItem(`timeTracker_${patientId}`);
    if (persistedStr) {
      try {
        const persisted = JSON.parse(persistedStr) as PersistedTimeTrackerState;
        if (Date.now() - persisted.sessionStartTime < 2 * 60 * 60 * 1000) {
          setSessionStatus(persisted.sessionStatus);
          setSessionStartTime(persisted.sessionStartTime);
          setLastModeSwitchTime(persisted.lastModeSwitchTime);
          setCurrentMode(persisted.currentMode);
          setRpmPrepTime(persisted.rpmPrepTime);
          setRpmCallTime(persisted.rpmCallTime);
          setRpmWrapTime(persisted.rpmWrapTime);
        } else {
          localStorage.removeItem(`timeTracker_${patientId}`);
        }
      } catch (error) {
        console.error("Failed to parse persisted time tracker state", error);
      }
    }
  }, [patientId]);

  // --- Update UI every second when session is running ---
  useEffect(() => {
    if (sessionStatus === "running") {
      const intervalId = setInterval(() => {
        setTick(Date.now());
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [sessionStatus]);

  // --- Persist session data every 10 seconds ---
  useEffect(() => {
    if (sessionStatus === "running" || sessionStatus === "review") {
      const persistInterval = setInterval(() => {
        persistSessionData();
      }, 10 * 1000);
      return () => clearInterval(persistInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionStatus,
    sessionStartTime,
    lastModeSwitchTime,
    currentMode,
    rpmPrepTime,
    rpmCallTime,
    rpmWrapTime,
    patientId,
  ]);

  // --- Prevent accidental closing via browser navigation ---
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (sessionStatus === "running" || sessionStatus === "review") {
        event.preventDefault();
        event.returnValue =
          "You have an unsaved time tracker session. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [sessionStatus]);

  const persistSessionData = () => {
    if (
      (sessionStatus === "running" || sessionStatus === "review") &&
      sessionStartTime &&
      lastModeSwitchTime
    ) {
      const persisted: PersistedTimeTrackerState = {
        sessionStatus,
        sessionStartTime,
        lastModeSwitchTime,
        currentMode,
        rpmPrepTime,
        rpmCallTime,
        rpmWrapTime,
        savedAt: Date.now(),
      };
      localStorage.setItem(`timeTracker_${patientId}`, JSON.stringify(persisted));
    }
  };

  // --- Time Calculations ---
  const getMainElapsedTime = (): number => {
    if (sessionStartTime && lastModeSwitchTime) {
      return sessionStatus === "running"
        ? rpmPrepTime + rpmCallTime + rpmWrapTime + Math.floor((Date.now() - lastModeSwitchTime) / 1000)
        : rpmPrepTime + rpmCallTime + rpmWrapTime;
    }
    return 0;
  };

  const formatTime = (seconds: number): string => {
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    return `${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
  };

  // --- Event Handlers ---
  const handleToggleSession = () => {
    if (sessionStatus === "idle") {
      const now = Date.now();
      setSessionStartTime(now);
      setLastModeSwitchTime(now);
      setSessionStatus("running");
    } else if (sessionStatus === "running") {
      if (lastModeSwitchTime) {
        const elapsed = Math.floor((Date.now() - lastModeSwitchTime) / 1000);
        switch (currentMode) {
          case "rpm-prep":
            setRpmPrepTime((prev) => prev + elapsed);
            break;
          case "rpm-call":
            setRpmCallTime((prev) => prev + elapsed);
            break;
          case "rpm-wrap":
            setRpmWrapTime((prev) => prev + elapsed);
            break;
          default:
            break;
        }
      }
      setSessionStatus("review");
    }
  };

  // When switching modes during a running session, record elapsed time
  const handleModeSwitch = (newMode: "rpm-prep" | "rpm-call" | "rpm-wrap") => {
    if (sessionStatus === "running" && newMode !== currentMode && lastModeSwitchTime) {
      const elapsed = Math.floor((Date.now() - lastModeSwitchTime) / 1000);
      switch (currentMode) {
        case "rpm-prep":
          setRpmPrepTime((prev) => prev + elapsed);
          break;
        case "rpm-call":
          setRpmCallTime((prev) => prev + elapsed);
          break;
        case "rpm-wrap":
          setRpmWrapTime((prev) => prev + elapsed);
          break;
        default:
          break;
      }
      setCurrentMode(newMode);
      setLastModeSwitchTime(Date.now());
    } else {
      setCurrentMode(newMode);
    }
  };

  // In review mode, clicking the stopwatch icon resumes the session.
  const handleContinue = () => {
    setLastModeSwitchTime(Date.now());
    setSessionStatus("running");
  };

  useEffect(() => {
    persistSessionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode, sessionStatus]);

  const handleSave = async () => {
    if (!sessionStartTime) return;
    const role = userCtx?.user?.role?.roletype === "physician" ? "physician" : "staff";
    const rpmPrepMinutes = rpmPrepTime / 60;
    const rpmCallMinutes = rpmCallTime / 60;
    const rpmWrapMinutes = rpmWrapTime / 60;
    try {
      if (rpmPrepMinutes > 0) {
        await PatientService.setTimeData(cookies.access_token, patientId, {
          type: "rpm-prep",
          start_time: new Date(sessionStartTime).toISOString(),
          minutes: rpmPrepMinutes,
          patientId,
          role,
        });
      }
      if (rpmCallMinutes > 0) {
        await PatientService.setTimeData(cookies.access_token, patientId, {
          type: "rpm-call",
          start_time: new Date(sessionStartTime).toISOString(),
          minutes: rpmCallMinutes,
          patientId,
          role,
        });
      }
      if (rpmWrapMinutes > 0) {
        await PatientService.setTimeData(cookies.access_token, patientId, {
          type: "rpm-wrap",
          start_time: new Date(sessionStartTime).toISOString(),
          minutes: rpmWrapMinutes,
          patientId,
          role,
        });
      }
      localStorage.removeItem(`timeTracker_${patientId}`);
      resetSession();
      onClose(patientId, new Date(sessionStartTime).toISOString());
    } catch (error) {
      console.error("Error saving time tracker data", error);
    }
  };

  const handleDiscard = () => {
    localStorage.removeItem(`timeTracker_${patientId}`);
    resetSession();
    onClose(null, null);
  };

  const resetSession = () => {
    localStorage.removeItem(`timeTracker_${patientId}`);
    setSessionStatus("idle");
    setSessionStartTime(null);
    setLastModeSwitchTime(null);
    setRpmPrepTime(0);
    setRpmCallTime(0);
    setRpmWrapTime(0);
    setCurrentMode("rpm-prep");
  };

  let timerLabel = "";
  switch (sessionStatus) {
    case "idle":
      timerLabel = `← ${t("Click to start time tracking")}`;
      break;
    case "review":
      timerLabel = `← ${t("Click stopwatch to continue")}`;
      break;
    case "running":
      timerLabel = `← ${t("Click to pause/stop")}`;
      break;
    default:
      timerLabel = "-";
  }

  // --- Rendering ---
  // When running, use the draggable paper
  if (sessionStatus === "running") {
    return (
      <Draggable handle=".time-tracker-header" nodeRef={timeTrackerDialogRef}>
        <Paper
          ref={timeTrackerDialogRef}
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            width: 500,
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Box
            className="time-tracker-header"
            sx={{
              backgroundColor: "success.main",
              color: "white",
              p: 1,
              cursor: "move",
              textAlign: "center",
            }}
          >
            <Typography sx={{ ml: 1, color: "white" }} variant="h6">
              {t("RPM Time Tracker")} – {t("Running")}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={handleToggleSession} color="error">
                  <TimerIcon sx={{ fontSize: 40 }} />
                </IconButton>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {timerLabel}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                {formatTime(getMainElapsedTime())}
              </Typography>
            </Box>
            {renderModePanels()}
          </Box>
        </Paper>
      </Draggable>
    );
  }

  // For idle/review, use a Dialog.
  return (
    <Dialog
      open={open}
      onClose={sessionStatus === "idle" ? () => onClose(null, null) : undefined}
      disableEscapeKeyDown={sessionStatus === "idle" ? false : true}
      PaperComponent={DraggablePaper}
    >
      <Box sx={{ width: 500 }}>
        <Box
          className="time-tracker-header"
          sx={{
            backgroundColor: sessionStatus === "review" ? "warning.main" : "primary.main",
            p: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ ml: 1, color: "white" }} variant="h6">
            {t("RPM Time Tracker")} {sessionStatus === "review" ? `– ${t("Review Session")}` : ""}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* In review mode, clicking the stopwatch icon resumes the session */}
              <IconButton
                onClick={sessionStatus === "review" ? handleContinue : handleToggleSession}
                color={"primary"}
              >
                <TimerIcon sx={{ fontSize: 40 }} />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 1 }}>
                {timerLabel}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
              {formatTime(getMainElapsedTime())}
            </Typography>
          </Box>
          {renderModePanels()}
          {sessionStatus === "review" && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button variant="outlined" color="error" onClick={handleDiscard}>
                {t("Discard")}
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                {t("Save")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default TimeTracker;
